import React, { useRef } from "react";
import useEnv, { LogLevel } from "../hooks/env";
import { HttpErrorType } from "../models/HttpErrorType";
import MessageSnackbar, { MessageSnackbarRef } from "../components/Alert/ErrorSnackbar/MessageSnackbar";

function throwUninitialized() {
  throw new Error("This context was not initialized.");
}

export enum SnackbarVisibility {
  HIDDEN,
  SHOWN,
}

export interface ErrorSnackbarContextType {
  clearSnackbar: () => void;
  showErrorSnackbar: (text: string) => void;
  showSnackbar: (text: string, type?: InfoType) => void;
  handleHttpError: (error?: HttpErrorType, snackbarVisibility?: SnackbarVisibility) => void;
  handleDialogNegativeCallback: (error?: HttpErrorType) => void;
}

export const ErrorSnackbarContext = React.createContext<ErrorSnackbarContextType>({
  clearSnackbar: throwUninitialized,
  showErrorSnackbar: throwUninitialized,
  showSnackbar: throwUninitialized,
  handleHttpError: throwUninitialized,
  handleDialogNegativeCallback: throwUninitialized,
});

type InfoType = "success" | "info" | "warning" | "error";

export const ErrorSnackbarContextProvider: React.FunctionComponent<{
  children?: JSX.Element;
}> = (props) => {
  const { log } = useEnv();
  const snackbar = useRef<MessageSnackbarRef>(null);

  const clearSnackbar = (): void => {
    snackbar.current?.clear();
  };

  const showSnackbar = (text: string, type: InfoType = "info", autoHide: boolean = true): void => {
    snackbar.current?.queue(text, type, autoHide);
  };

  const showErrorSnackbar = (text: string): void => {
    showSnackbar(text, "error");
  };

  const handleHttpError = (
    error?: HttpErrorType,
    snackbarVisibility: SnackbarVisibility = SnackbarVisibility.SHOWN,
  ) => {
    const data = error?.response?.data;
    const message = data?.message || error?.message || "Unknown error occurred.";
    const multipleErrors =
      data?.errors ||
      [data?.propertyViolations, data?.parameterViolations, data?.classViolations, data?.returnValueViolations]
        .flatMap((x) => x || [])
        .filter(Boolean)
        .map((violation) => violation.message as string);
    if (error?.name === "HiddenError" || snackbarVisibility === SnackbarVisibility.HIDDEN) {
      return;
    }
    log(LogLevel.ERROR, message, error);
    multipleErrors.forEach((errorMessage) => log(LogLevel.ERROR, errorMessage));
    if (multipleErrors.length > 0) {
      multipleErrors.forEach(showErrorSnackbar);
    } else {
      showErrorSnackbar(message);
    }
  };

  const handleDialogNegativeCallback = (error?: HttpErrorType) => {
    if (error) {
      handleHttpError(error);
    } else {
      log(LogLevel.DEBUG, "Canceled dialog.");
    }
  };

  const { children } = props;

  return (
    <ErrorSnackbarContext.Provider
      value={{
        clearSnackbar,
        showErrorSnackbar,
        showSnackbar,
        handleHttpError,
        handleDialogNegativeCallback,
      }}
    >
      {children}
      <MessageSnackbar ref={snackbar} />
    </ErrorSnackbarContext.Provider>
  );
};

export default ErrorSnackbarContext;
